import { breakpointsTailwind } from '@vueuse/core'
import { defineChart } from '..'
import { tooltip } from '@/composables/useChart/theme'
import { useRegionalView } from '@/composables/useParams/params/regional-view'
import type { Series } from './log-bar'
import type { Values } from '@/composables/useValues'

/**
 * People icon.
 */
const peopleIcon = `path://M 124 92 A 41 41 0 0 1 141 96 C 157 104 166 119 166 135 V 250 C 163 268 142 263 142 250 V 142 C 140 135 128 138 128 142 V 390 C 125 415 92 408 91 390 V 237 C 89 228 75 232 75 238 C 75 288 75 339 75 390 C 72 415 39 408 38 390 L 38 142 C 36 136 25 138 24 142 V 250 C 22 268 1 263 0 250 V 135 C 0 119 6 103 20 96 Q 27 92 37 92 Z M 123 40 A 40 40 0 1 1 83 0 A 40 40 0 0 1 123 40 Z`

/**
 * Ratio of the icon.
 */
const ratio = 166 / 406

type Params = [Values, Series]

export default defineChart<Params>(
    ({ filtered, countryData, compareCountryData, dimension }, series) => {
        if (!dimension || !filtered || !countryData) return

        /**
         * Use countries.
         */
        const { getName, getPrevalenceRanking, getCountriesInRegion } =
            useCountries()

        /**
         * Filter the data by region if needed.
         */
        const values = computed(() => {
            if (!unref(useRegionalView())) return filtered

            const countries = getCountriesInRegion(countryData.iso) ?? []
            return filtered.filter(({ iso }) => countries.includes(iso))
        })

        /**
         * Sort the data by prevalence ranking.
         */
        const sorted = computed(() => {
            return unref(values).sort((a, b) => {
                return getPrevalenceRanking(b.iso) - getPrevalenceRanking(a.iso)
            })
        })

        /**
         * Index of the current country inside the sorted array of values.
         */
        const currentIndex = unref(sorted).indexOf(countryData)

        /**
         * To which we add the two countries before and after, as well
         * as the optional comparing country.
         */
        const range = unref(sorted).slice(
            Math.max(0, currentIndex - 2),
            currentIndex + 3
        )

        if (compareCountryData && !range.includes(compareCountryData)) {
            const compareIndex = range
                .slice()
                .concat(compareCountryData)
                .sort((a, b) => a.score - b.score)
                .indexOf(compareCountryData)

            range.splice(compareIndex, 0, compareCountryData)
        }

        /**
         * We sort it again and turn that into ECharts compatible
         * data.
         */
        const barData = range.map(value => ({
            name: getName(value.iso) ?? '',
            code: value.iso,
            value: value[series.valueKey],
            rank: getRank(value.iso, compareCountryData?.iso),
            itemStyle: {
                color:
                    value === compareCountryData
                        ? dimension?.comparisonCountryColor
                        : value === countryData
                        ? dimension?.countryColor
                        : twColor('gray')
            }
        }))

        // Reverse order if needed.
        barData.reverse()

        /**
         * Maximum value of the X axis.
         */
        const max = Math.max(...barData.map(({ value }) => value))

        return {
            tooltip: {},
            grid: {
                containLabel: true,
                left: '5%'
            },
            xAxis: {
                type: 'value',
                id: 'real',
                max: series.type === 'log' ? 150 : max,
                min: 0,
                position: 'top',
                splitLine: {
                    show: true
                },
                show: true
            },
            dataZoom: {
                xAxisIndex: 0,
                filterMode: 'none',
                startValue: 0,
                height: 10,
                bottom: '2.5%',
                showDataShadow: true,
                brushSelect: false,
                maxValueSpan: series.maxDisplay,
                disabled: series.valueKey === 'value',
                show: series.valueKey !== 'value'
            },
            media: [
                {
                    query: {
                        maxWidth: breakpointsTailwind.sm
                    },
                    option: {
                        yAxis: {
                            data: barData.map(({ rank, code }) => {
                                return `{rank|#${
                                    rank.split(' ').at(0) || ''
                                }}\n${code}`
                            })
                        }
                    }
                },
                {
                    query: {
                        minWidth: breakpointsTailwind.sm
                    },
                    option: {
                        yAxis: {
                            data: barData.map(({ rank, name }) => {
                                return `{rank|#${rank}}\n${name}`
                            })
                        }
                    }
                }
            ],
            yAxis: {
                type: 'category',
                position: 'left',
                z: 1000,
                axisLine: {
                    show: false
                },
                axisTick: {
                    show: false
                },
                data: barData.map(({ rank, name }) => {
                    return `{rank|#${rank}}\n${name}`
                }),
                axisLabel: {
                    rich: {
                        rank: {
                            fontWeight: 'bold'
                        }
                    },
                    margin: 20,
                    verticalAlign: 'middle',
                    overflow: 'break',
                    show: true,
                    lineHeight: 16,
                    align: 'right'
                }
            },
            export: {
                grid: {
                    top: '10%',
                    bottom: '10%'
                },
                dataZoom: {
                    show: false
                }
            },
            series: [
                {
                    id: 'hidden',
                    type: 'bar',
                    data: barData.map(({ value }) => value),
                    silent: true,
                    itemStyle: { opacity: 0 },
                    animation: false,
                    z: 1000,
                    barGap: '-100%',
                    label: {
                        position: 'right',
                        show: true,
                        opacity: 1,
                        offset: [20, 0],
                        align: 'left',
                        color: 'black',
                        padding: [5, 10, 5, 10],
                        borderColor: 'black',
                        borderWidth: 1,
                        backgroundColor: 'white',
                        formatter(params) {
                            return series.formatter(
                                Number(barData[params.dataIndex].value)
                            )
                        }
                    }
                },
                {
                    id: 'visible',
                    type: series.valueKey === 'score' ? 'pictorialBar' : 'bar',
                    tooltip: {
                        formatter(params) {
                            return tooltip(
                                series.name,
                                params.name,
                                series.formatter(Number(params.value))
                            )
                        }
                    },
                    symbol: peopleIcon,
                    symbolSize: [`${100 * ratio}%`, '100%'],
                    symbolClip: true,
                    maxBarWidth: '80%',
                    minBarWidth: '60%',
                    data: barData.map(data => {
                        return {
                            ...data,
                            value: data.value,
                            symbolRepeat: data.value,
                            symbolBoundingData: data.value
                        }
                    }),
                    itemStyle: {
                        color: twColor('sun-yellow')
                    },
                    universalTransition: {
                        enabled: true
                    }
                }
            ]
        }
    }
)

/**
 * Get the country rank.
 */
function getRank(iso: string, compareIso?: string) {
    const { getPrevalenceRanking, getRegion } = useCountries()

    const rank = getPrevalenceRanking(iso)

    const regionName =
        unref(useRegionalView()) &&
        compareIso === iso &&
        getRegion(compareIso)?.name

    return `${rank} ${regionName ? `(${regionName})` : ''}`
}
